import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { HistoryIcon } from '@solar-icons/react/linear/history'
import { PlayIcon } from '@solar-icons/react/bold/play'
import { MagnifierIcon } from '@solar-icons/react/linear/magnifier'
import { TrashBinTrashIcon } from '@solar-icons/react/linear/trash-bin-trash'
import {
  Badge,
  Button,
  ConfirmButton,
  EmptyState,
  Input,
  SegmentedControl,
} from '@/components/ui'
import { useHistoryStore } from '@/stores/historyStore'
import { useSavedStore } from '@/stores/savedStore'
import { formatCount, formatDuration, formatRelativeTime } from '@/lib/format/cells'
import { useRunQuery } from '@/lib/hooks/useRunQuery'
import { useLocale } from '@/lib/hooks/useLocale'
import { shortId } from '@/lib/utils/id'

type StatusFilter = 'all' | 'success' | 'error'

export function HistoryView() {
  const navigate = useNavigate()
  const locale = useLocale()

  const entries = useHistoryStore((state) => state.entries)
  const remove = useHistoryStore((state) => state.remove)
  const clear = useHistoryStore((state) => state.clear)
  const saveQuery = useSavedStore((state) => state.save)
  const { run } = useRunQuery()

  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<StatusFilter>('all')

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase()
    return entries.filter((entry) => {
      if (status !== 'all' && entry.status !== status) return false
      if (!needle) return true
      return entry.query.toLowerCase().includes(needle)
    })
  }, [entries, search, status])

  // Submit here and then navigate: the thread and the in-flight request both
  // live outside the routed views, so nothing needs to be handed across.
  const rerun = (query: string) => {
    void run(query)
    navigate('/')
  }

  return (
    <div className="scrollbar-thin h-full overflow-y-auto">
      <div className="mx-auto w-full max-w-4xl space-y-4 px-4 py-6">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-56 flex-1">
            <MagnifierIcon
              size={14}
              className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-subtle"
            />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Filter queries…"
              className="pl-9"
              aria-label="Filter history"
            />
          </div>

          <SegmentedControl
            size="sm"
            ariaLabel="Filter by status"
            value={status}
            onChange={setStatus}
            options={[
              { value: 'all', label: 'All' },
              { value: 'success', label: 'Succeeded' },
              { value: 'error', label: 'Failed' },
            ]}
          />

          <ConfirmButton
            onConfirm={clear}
            disabled={entries.length === 0}
            confirmLabel="Delete all history?"
            icon={<TrashBinTrashIcon size={14} strokeWidth={1.75} />}
          >
            Clear
          </ConfirmButton>
        </div>

        {filtered.length === 0 ? (
          <EmptyState
            icon={<HistoryIcon size={20} strokeWidth={1.75} />}
            title={entries.length === 0 ? 'No history yet' : 'Nothing matches'}
            description={
              entries.length === 0
                ? 'Queries you run are recorded here, in this browser only. Turn the behaviour off in Settings if you would rather not keep them.'
                : 'Try a different search term or status filter.'
            }
          />
        ) : (
          <ul className="space-y-2">
            {filtered.map((entry) => (
              <li
                key={entry.id}
                className="group rounded-xl border border-line bg-surface px-4 py-3 transition-colors hover:border-line-strong"
              >
                <div className="flex items-start gap-3">
                  <button
                    type="button"
                    onClick={() => rerun(entry.query)}
                    className="min-w-0 flex-1 text-left"
                    title="Run this query again"
                  >
                    <p className="truncate text-sm font-medium text-fg">
                      {entry.query}
                    </p>
                    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                      <Badge tone={entry.status === 'success' ? 'success' : 'danger'}>
                        {entry.status}
                      </Badge>
                      {typeof entry.rowCount === 'number' && (
                        <Badge>{formatCount(entry.rowCount, locale)} rows</Badge>
                      )}
                      {typeof entry.durationMs === 'number' && (
                        <Badge>{formatDuration(entry.durationMs)}</Badge>
                      )}
                      <Badge title={`Conversation ${entry.conversationId}`}>
                        {shortId(entry.conversationId, 6)}
                      </Badge>
                      <span className="text-[11px] text-subtle">
                        {formatRelativeTime(entry.at)}
                      </span>
                    </div>
                    {entry.errorMessage && (
                      <p className="mt-1.5 line-clamp-2 text-xs text-muted">
                        {entry.errorMessage}
                      </p>
                    )}
                  </button>

                  <div className="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                    <Button
                      variant="ghost"
                      size="sm"
                      title="Save for reuse"
                      aria-label="Save for reuse"
                      onClick={() => saveQuery({ query: entry.query })}
                    >
                      Save
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={<PlayIcon size={13} />}
                      title="Run again"
                      aria-label="Run again"
                      onClick={() => rerun(entry.query)}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={<TrashBinTrashIcon size={13} strokeWidth={1.75} />}
                      title="Remove from history"
                      aria-label="Remove from history"
                      onClick={() => remove(entry.id)}
                    />
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
